import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";
import { LogoWordmark } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      // Detección de móvil ANTES del login para forzar `remember=true`
      // (queremos que el perito no tenga que volver a entrar durante
      // al menos 30 días).
      const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const isNarrow = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
      const isMobile = isMobileUA || isNarrow;
      const effectiveRemember = isMobile ? true : remember;
      await login(email, password, effectiveRemember);
      navigate(isMobile ? "/m/agenda" : "/analytics");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="px-6 sm:px-10 py-6">
        {/* "Volver a la web" oculto mientras la landing pública no esté
            disponible: la raíz redirige aquí, así que ese link generaba
            un bucle. Cuando se reactive la landing, restaurarlo. */}
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-6 pb-16">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center gap-2 mb-10">
            <LogoWordmark />
          </div>

          <div className="text-center mb-8">
            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-zinc-900">
              Acceder a tu gabinete
            </h1>
            <p className="text-xs text-zinc-500 mt-1.5">Introduce tus credenciales para entrar.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="email" className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-zinc-600">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="tu@gabinete.es"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="!text-sm"
              />
            </div>
            <div>
              <label htmlFor="password" className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-zinc-600">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="!pr-10 !text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  data-testid="toggle-password-visibility"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900 p-1 rounded"
                  aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPwd ? <EyeSlash className="w-4 h-4" weight="bold" /> : <Eye className="w-4 h-4" weight="bold" />}
                </button>
              </div>
            </div>

            {err && (
              <div
                className="text-xs px-3 py-2 rounded-md bg-rose-50 text-rose-700 border border-rose-200 animate-fade-in-down"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <label className="flex items-center gap-2 text-[12px] text-zinc-700 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="login-remember"
                className="w-4 h-4 accent-brand cursor-pointer"
              />
              Recordar sesión 30 días
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="btn-brand w-full rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="dot-blink"><span/><span/><span/></span>
              ) : (
                <>Entrar <ArrowRight className="w-4 h-4" weight="bold" /></>
              )}
            </button>
          </form>

          <p className="text-[11px] text-zinc-500 mt-6 text-center leading-relaxed">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="font-semibold text-brand hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
